export interface AssignmentReminderPolicy {
  /** Hard cap on reminder dispatches per assignment. Range 0–3. */
  max: number;
  /**
   * Hours before appointment start to fire each reminder.
   * Strictly decreasing. Length ≤ max.
   */
  schedule_hours_before_appointment: number[];
  /**
   * Hours before appointment start to fire the one-shot escalation.
   * Must be ≤ min(schedule_hours_before_appointment).
   */
  escalate_hours_before: number;
}

export const SYSTEM_DEFAULT_POLICY: AssignmentReminderPolicy = Object.freeze({
  max: 2,
  schedule_hours_before_appointment: [24, 4],
  escalate_hours_before: 2,
}) as AssignmentReminderPolicy;

/** Hard upper bound on policy.max and schedule_hours_before_appointment length. */
export const MAX_REMINDER_SLOTS = 3;

export type PolicyValidationResult =
  | { ok: true; policy: AssignmentReminderPolicy }
  | { ok: false; errors: string[] };

export type ReminderTarget = "staff";

export interface ReminderAssignmentState {
  reminder_count: number;
  escalated_at: Date | null;
}

export interface ReminderAppointmentState {
  start_time: Date;
}

export interface NextReminderDecision {
  dueAt: Date | null;
  isEscalation: boolean;
  target: ReminderTarget;
}

/**
 * Validate a candidate AssignmentReminderPolicy. Returns the canonical
 * (immutable, slot-trimmed) shape on success or an error list on failure.
 */
export function validateReminderPolicyShape(
  input: unknown,
): PolicyValidationResult {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, errors: ["Policy must be an object."] };
  }
  const record = input as Record<string, unknown>;
  const errors: string[] = [];

  const max = record.max;
  if (
    typeof max !== "number" ||
    !Number.isInteger(max) ||
    max < 0 ||
    max > MAX_REMINDER_SLOTS
  ) {
    errors.push(`'max' must be an integer in [0, ${MAX_REMINDER_SLOTS}].`);
  }

  const schedule = record.schedule_hours_before_appointment;
  if (!Array.isArray(schedule)) {
    errors.push("'schedule_hours_before_appointment' must be an array.");
  } else {
    if (schedule.length > MAX_REMINDER_SLOTS) {
      errors.push(
        `'schedule_hours_before_appointment' may contain at most ${MAX_REMINDER_SLOTS} entries.`,
      );
    }
    if (typeof max === "number" && schedule.length > max) {
      errors.push(
        "'schedule_hours_before_appointment' has more entries than 'max'.",
      );
    }
    for (let i = 0; i < schedule.length; i++) {
      const v = schedule[i];
      if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) {
        errors.push(
          `'schedule_hours_before_appointment[${i}]' must be a positive number.`,
        );
      }
    }
    for (let i = 1; i < schedule.length; i++) {
      const prev = schedule[i - 1];
      const cur = schedule[i];
      if (typeof prev === "number" && typeof cur === "number" && cur >= prev) {
        errors.push(
          "'schedule_hours_before_appointment' must be strictly decreasing.",
        );
        break;
      }
    }
  }

  const escalate = record.escalate_hours_before;
  if (
    typeof escalate !== "number" ||
    !Number.isFinite(escalate) ||
    escalate <= 0
  ) {
    errors.push("'escalate_hours_before' must be a positive number.");
  } else if (Array.isArray(schedule) && schedule.length > 0) {
    const min = Math.min(
      ...schedule.filter((v): v is number => typeof v === "number"),
    );
    if (Number.isFinite(min) && escalate > min) {
      errors.push(
        "'escalate_hours_before' must be ≤ the smallest reminder hour.",
      );
    }
  }

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    policy: {
      max: max as number,
      schedule_hours_before_appointment: (schedule as number[]).slice(),
      escalate_hours_before: escalate as number,
    },
  };
}

function coerceToPolicy(input: unknown): AssignmentReminderPolicy {
  const result = validateReminderPolicyShape(input);
  if (result.ok) return result.policy;
  return SYSTEM_DEFAULT_POLICY;
}

/**
 * Decide what (if anything) is due now for an assignment.
 * Pure function — no side effects, no I/O.
 */
export function nextReminderDue(
  policy: AssignmentReminderPolicy | unknown,
  assignment: ReminderAssignmentState,
  appointment: ReminderAppointmentState,
  now: Date,
): NextReminderDecision {
  const safePolicy = coerceToPolicy(policy);
  const startMs = appointment.start_time.getTime();
  const schedule = safePolicy.schedule_hours_before_appointment
    .slice(0, safePolicy.max)
    .filter((h) => typeof h === "number" && Number.isFinite(h) && h > 0);

  schedule.sort((a, b) => b - a);

  if (assignment.reminder_count < schedule.length) {
    const slotHours = schedule[assignment.reminder_count] ?? 0;
    const dueAt = new Date(startMs - slotHours * 60 * 60 * 1000);
    if (now.getTime() >= dueAt.getTime()) {
      return { dueAt, isEscalation: false, target: "staff" };
    }
    return { dueAt: null, isEscalation: false, target: "staff" };
  }

  if (assignment.escalated_at !== null) {
    return { dueAt: null, isEscalation: false, target: "staff" };
  }
  const escalationDueAt = new Date(
    startMs - safePolicy.escalate_hours_before * 60 * 60 * 1000,
  );
  if (now.getTime() >= escalationDueAt.getTime()) {
    return { dueAt: escalationDueAt, isEscalation: true, target: "staff" };
  }
  return { dueAt: null, isEscalation: false, target: "staff" };
}

/** Render a plain-English summary of a reminder policy. */
export function describeReminderPolicy(
  policy: AssignmentReminderPolicy,
): string {
  const parts: string[] = [];
  const schedule = policy.schedule_hours_before_appointment.slice(
    0,
    policy.max,
  );
  if (schedule.length === 0) {
    parts.push("Staff reminder: none.");
  } else {
    const slotList = schedule.map((h) => `${h}h before`).join(", ");
    parts.push(`Staff reminder: ${slotList}.`);
  }
  parts.push(`Escalate ${policy.escalate_hours_before}h before.`);
  return parts.join(" ");
}
